import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { ListIcon } from "@phosphor-icons/react/dist/csr/List";
import { Button } from "@volca/ui/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@volca/ui/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { TooltipShortcut } from "@volca/ui/components/ui/tooltip-shortcut";
import { cn } from "@volca/ui/lib/utils";
import useDelayedAction from "@/core/hooks/use-delayed-action";
import { IAiSpreadsheetSheet } from "@/payroll/ai-script/types";
import { SHEET_PREFETCH_DWELL_MS } from "./utils";

interface SheetSwitcherProps {
  activeSheetId: string;
  isOpen: boolean;
  sheets: IAiSpreadsheetSheet[];
  onOpenChange: (isOpen: boolean) => void;
  onSheetSelect: (sheetId: string) => void;
  onSheetPrefetchIntent: (sheetId: string) => void;
}

export const SheetSwitcher = ({
  activeSheetId,
  isOpen,
  sheets,
  onOpenChange,
  onSheetSelect,
  onSheetPrefetchIntent,
}: SheetSwitcherProps) => {
  const { schedule: scheduleSheetPrefetch, cancel: cancelSheetPrefetch } =
    useDelayedAction<string>(onSheetPrefetchIntent, SHEET_PREFETCH_DWELL_MS);

  const handleSheetSelect = (sheetId: string) => {
    onSheetSelect(sheetId);
    onOpenChange(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <TooltipProvider delay={200}>
        <Tooltip>
          <TooltipTrigger
            render={
              <PopoverTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Search sheets"
                    className="mx-1 self-center"
                  />
                }
              />
            }
          >
            <ListIcon />
          </TooltipTrigger>
          <TooltipContent side="top">
            <TooltipShortcut keys={["mod", "K"]}>Search sheets</TooltipShortcut>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent align="start" side="top" className="w-80 p-0">
        <Command>
          <CommandInput placeholder="Search sheets..." />
          <CommandList className="max-h-72 overflow-y-auto overscroll-contain">
            <CommandEmpty>No sheets found.</CommandEmpty>
            <CommandGroup heading="Sheets">
              {sheets.map((sheet) => {
                const isActive = sheet.id === activeSheetId;

                return (
                  <CommandItem
                    key={sheet.id}
                    value={`${sheet.name} ${sheet.id}`}
                    onSelect={() => handleSheetSelect(sheet.id)}
                    className="min-w-0"
                    onMouseEnter={() => scheduleSheetPrefetch(sheet.id)}
                    onMouseLeave={cancelSheetPrefetch}
                    onFocus={() => scheduleSheetPrefetch(sheet.id)}
                    onBlur={cancelSheetPrefetch}
                  >
                    <span
                      className="min-w-0 flex-1 truncate"
                      title={sheet.name}
                    >
                      {sheet.name}
                    </span>
                    <CheckIcon
                      className={cn("h-4 w-4 text-brand", {
                        "opacity-100": isActive,
                        "opacity-0": !isActive,
                      })}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
